import { blowsSoon, cellCenter, moveRoute, moveTarget, type BattleState, type Hero } from '@somnhold/sim';

/** `?predict=0`: my hero waits for the room to walk, as before, to compare side by side. */
export const PREDICT_OFF = typeof location !== 'undefined' && new URLSearchParams(location.search).get('predict') === '0';
/** No answer from the room by then (the order was lost or refused): stop guessing. */
const GIVE_UP_MS = 1500;
/** A guess that didn't come true glides back to the real hero over this long. */
const BLEND_MS = 150;
/** The room's destination counts as the one I ordered when it lands this close (sim units; a hex is ~1000). */
const SAME_DEST = 600;
/**
 * Ticks before a marked blow lands (any of them: `blowsSoon`) in which the drawing gives up its head start when it
 * and the real hero stand on different sides of the line: the blow hits where the room has my hero, so drawn
 * safely outside a nova it read as "it hits outside the circle". It shrinks to nothing by the moment it lands.
 */
const HONEST_TICKS = 5;

type Pt = { x: number; y: number };

/** What the screen shows of my hero instead of the simulation: where, whether it walks, which way (0..1023, as `Hero.facing`). */
export interface Lead {
  x: number;
  y: number;
  walk: boolean;
  facing: number;
}

const len = (pts: Pt[]): number => {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += Math.hypot((pts[i] as Pt).x - (pts[i - 1] as Pt).x, (pts[i] as Pt).y - (pts[i - 1] as Pt).y);
  return d;
};

/** The point `d` along a polyline (its end, past it) and the heading there. */
const along = (pts: Pt[], d: number): { x: number; y: number; dx: number; dy: number } => {
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as Pt;
    const b = pts[i] as Pt;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (seg > 0 && d <= seg) return { x: a.x + ((b.x - a.x) * d) / seg, y: a.y + ((b.y - a.y) * d) / seg, dx: b.x - a.x, dy: b.y - a.y };
    d -= seg;
  }
  const last = pts[pts.length - 1] as Pt;
  const prev = pts.length > 1 ? (pts[pts.length - 2] as Pt) : last;
  return { x: last.x, y: last.y, dx: last.x - prev.x, dy: last.y - prev.y };
};

/** Ticks until the soonest blow on which the drawing (`a`) and the real hero disagree, if one lands within HONEST_TICKS. */
const torn = (s: BattleState, a: Pt, simX: number, simY: number): number | null => {
  let soon: number | null = null;
  for (const b of blowsSoon(s, HONEST_TICKS)) {
    const left = b.at - s.tick;
    if (soon !== null && left >= soon) continue;
    // Rounded like the simulation's positions: the tests take whole sim units.
    if (b.hits(Math.round(a.x), Math.round(a.y)) !== b.hits(simX, simY)) soon = Math.max(0, left);
  }
  return soon;
};

const facingOf = (dx: number, dy: number, fallback: number): number => (dx === 0 && dy === 0 ? fallback : (Math.round((Math.atan2(dy, dx) / (Math.PI * 2)) * 1024) + 1024) % 1024);

/**
 * Live play hides the round trip from my own walking (as Diablo does, unlike the lockstep underneath): a tap
 * starts my hero walking on screen at once, along the path the simulation will take (same A*, same speed),
 * and once the room's tick makes it walk for real the drawing keeps the head start it had along the real
 * path, arriving that much earlier. Only the drawing changes: the simulation, everyone else and every blow
 * are untouched. A guess that doesn't come true (stunned, the order lost) glides back to the real hero.
 */
export class MovePredictor {
  private plan: { pts: Pt[]; at: number; speed: number; target: Pt; was: Pt; lead: number | null; full: number } | null = null;
  /** How far the drawing was from the real hero when a guess was dropped, and when: it glides back from there. */
  private back: { dx: number; dy: number; at: number } | null = null;
  private last: Lead | null = null;

  /** A guess (or the glide back from one) is on screen: draw every frame, even between ticks. */
  get busy(): boolean {
    return this.plan !== null || this.back !== null;
  }

  /** A move order just went out to the room. `shown`: where my hero is drawn now (sim units). */
  moved(s: BattleState, h: Hero, shown: Pt, x: number, y: number, now: number): void {
    if (PREDICT_OFF || h.downTicks > 0 || h.stun > 0 || (h.shove ?? 0) > 0) return;
    const R = s.map.radius;
    // The simulation's own walk (orderMove): the same target and the same cells, so the guess never cuts a corner.
    const tgt = moveTarget(s, shown, x, y);
    const route = moveRoute(s, shown, tgt.x, tgt.y);
    if (!route) return;
    const { path } = route;
    const dest = { x: route.x, y: route.y };
    const pts: Pt[] = [shown, ...path.slice(0, -1).map((c) => cellCenter(R, c)), dest];
    this.plan = { pts, at: now, speed: h.stats.speed, target: dest, was: { x: h.destX, y: h.destY }, lead: null, full: 0 };
    this.back = null;
  }

  /** Something else was ordered (an attack, a skill): my hero does what the room says, gliding back to it. */
  cancel(simX: number, simY: number, now: number): void {
    if (this.plan && this.last) this.drop(this.last, simX, simY, now);
    this.plan = null;
  }

  /** Where to draw my hero this frame, given where the simulation's glide puts it; null: where the simulation says. */
  lead(s: BattleState, h: Hero, simX: number, simY: number, now: number): Lead | null {
    const p = this.plan;
    if (p) {
      const out = this.follow(s, p, h, simX, simY, now);
      this.last = out;
      if (out) return out;
    }
    const b = this.back;
    if (!b) return null;
    const k = 1 - (now - b.at) / BLEND_MS;
    if (k <= 0) {
      this.back = null;
      return null;
    }
    return { x: simX + b.dx * k, y: simY + b.dy * k, walk: h.anim === 'walk', facing: h.facing };
  }

  private follow(s: BattleState, p: NonNullable<MovePredictor['plan']>, h: Hero, simX: number, simY: number, now: number): Lead | null {
    const stay = this.last ?? { x: simX, y: simY, walk: false, facing: h.facing };
    // Stunned or thrown back by an ogre: the room moves it, not my order.
    if (h.downTicks > 0 || h.stun > 0 || (h.shove ?? 0) > 0) return this.drop(stay, simX, simY, now);
    // The room took my order once its destination is the one I asked for: a new one, or the same as before (a second tap there).
    const near = (a: Pt): boolean => Math.abs(a.x - p.target.x) + Math.abs(a.y - p.target.y) <= SAME_DEST;
    const mine = p.lead !== null || (near({ x: h.destX, y: h.destY }) && (h.destX !== p.was.x || h.destY !== p.was.y || near(p.was)));
    if (!mine) {
      // On its way to the room: walk the guessed path from where the tap found my hero.
      if (now - p.at > GIVE_UP_MS) return this.drop(stay, simX, simY, now);
      const d = (p.speed * (now - p.at)) / 1000;
      const a = along(p.pts, d);
      if (torn(s, a, simX, simY) !== null) return this.drop(a, simX, simY, now);
      return { x: a.x, y: a.y, walk: d < len(p.pts), facing: facingOf(a.dx, a.dy, h.facing) };
    }
    // The room made it walk: the same path for real, the drawing still as far ahead on it as it got.
    const R = s.map.radius;
    const real: Pt[] = [{ x: simX, y: simY }, ...h.path.slice(0, -1).map((c) => cellCenter(R, c)), { x: h.destX, y: h.destY }];
    const left = len(real);
    if (p.lead === null) p.full = p.lead = Math.max(0, left - Math.max(0, len(p.pts) - (p.speed * (now - p.at)) / 1000));
    // A blow about to land between the drawing and the real hero: give up the head start for good (no jump forward after).
    const soon = torn(s, along(real, Math.min(left, p.lead)), simX, simY);
    if (soon !== null) p.lead = Math.min(p.lead, (p.full * soon) / HONEST_TICKS);
    if (!h.moving && left < 1) {
      this.plan = null;
      return null;
    }
    const a = along(real, Math.min(left, p.lead));
    return { x: a.x, y: a.y, walk: h.moving || p.lead < left, facing: facingOf(a.dx, a.dy, h.facing) };
  }

  private drop(at: Pt, simX: number, simY: number, now: number): null {
    this.plan = null;
    this.back = { dx: at.x - simX, dy: at.y - simY, at: now };
    return null;
  }
}
